import * as React from 'react'
import { TooltipProps } from 'recharts'
import * as RechartsPrimitive from 'recharts'

import { commafy } from '@hap/utils'

import { cn } from '../utils'

interface TooltipHeaderProps {
  title: React.ReactNode
  subValue?: React.ReactNode | number
  subLabel?: React.ReactNode
}

export function ChartTooltipHeader(props: TooltipHeaderProps) {
  return (
    <header className="mb-3 grid shrink-0 gap-1 p-3 pb-0 leading-none">
      <div className="font-semibold">{props.title}</div>
      {typeof props.subValue !== 'undefined' && (
        <div>
          <ChartTooltipMetric value={props.subValue as unknown as string} />
          &nbsp;{props.subLabel}
        </div>
      )}
    </header>
  )
}

export interface ChartTooltipWrapperProps extends TooltipProps<string, string | number> {
  subValueKey?: string
  subLabel?: string
  dataKeys?: Array<
    | {
        label: string
        formatter: (value: string | number) => string | number
      }
    | string
  >
}

export function ChartTooltipMetric(props: {
  value?: number | string | null
  unit?: React.ReactNode
  precision?: number
  className?: string
}) {
  return (
    <span
      className={cn(
        'bg-secondary inline-block rounded-md p-1 font-mono',
        props.className,
      )}
    >
      {typeof props.value === 'number' && isNaN(props.value)
        ? '-'
        : commafy(props.value, props.precision)}
      {props.unit}
    </span>
  )
}

export function ChartTooltipLineItem(props: {
  keyColor?: string
  label?: string | number
  value?: string | number | null
  subValue?: string | number | null
  subUnit?: React.ReactNode
  unit?: React.ReactNode
  precision?: number
}) {
  const precision = props.precision ?? 1
  return (
    <div className="flex items-center gap-4">
      {props.keyColor && (
        <div
          className="h-3 w-3 shrink-0 rounded-md"
          style={{ backgroundColor: props.keyColor }}
        ></div>
      )}
      <div className="grow">{props.label}</div>
      <div className="flex gap-2">
        {typeof props.subValue !== 'undefined' && (
          <ChartTooltipMetric
            value={props.subValue}
            unit={props.subUnit}
            precision={precision}
            className="bg-secondary/40"
          />
        )}
        <ChartTooltipMetric value={props.value} unit={props.unit} precision={precision} />
      </div>
    </div>
  )
}

export function ChartTooltipWrapper(props: React.PropsWithChildren) {
  return (
    <div className="bg-popover gap-sm flex min-w-[200px] flex-col rounded-md border drop-shadow-lg">
      {props.children}
    </div>
  )
}
/*
export function ChartTooltipContent(props: PropsWithChildren) {
  return (
    <div className="grid gap-2 overflow-y-auto pt-0 p-6 grow shrink">
      {props.children}
    </div>
  )
}

export function ChartTooltip(props: PropsWithChildren<ChartTooltipWrapperProps>) {
  const subLabelValue = useMemo(() => {
    if (props.subValueKey && props.payload) {
      return props.payload[0]?.payload[props.subValueKey]
    }
    return
  }, [props])

  return (
    <ChartTooltipWrapper>
      <ChartTooltipHeader
        title={props.label}
        subLabel={props.subLabel}
        subValue={subLabelValue}
      />
      <ChartTooltipContent>
        {props.children
          ? props.children
          : props.payload?.map((p) => {
              return (
                <ChartTooltipLineItem
                  key={p.dataKey}
                  keyColor={p.color}
                  label={p.dataKey}
                  value={p.value}
                  unit={p.unit}
                />
              )
            })}
      </ChartTooltipContent>
    </ChartTooltipWrapper>
  )
}*/

export const X_AXIS_DEFAULT_PROPS: Omit<
  React.ComponentPropsWithoutRef<typeof RechartsPrimitive.XAxis>,
  'dangerouslySetInnerHTML'
> = {
  minTickGap: 10,
  interval: 'equidistantPreserveStart',
  tickMargin: 10,
}

export const Y_AXIS_DEFAULT_PROPS: Omit<
  React.ComponentPropsWithoutRef<typeof RechartsPrimitive.YAxis>,
  'dangerouslySetInnerHTML'
> = {
  interval: 'preserveEnd',
  allowDecimals: false,
}

// Format: { THEME_NAME: CSS_SELECTOR }
const THEMES = { light: '', dark: '.dark' } as const

export type ChartConfig = {
  [k in string]: {
    label?: React.ReactNode
    icon?: React.ComponentType
    unit?: '%' | 's'
    hidden?: boolean
    formatter?: (v: number) => number | string
  } & (
    | { color?: string; theme?: never }
    | { color?: never; theme: Record<keyof typeof THEMES, string> }
  )
}

type ChartContextProps = {
  config: ChartConfig
}

const ChartContext = React.createContext<ChartContextProps | null>(null)

function useChart() {
  const context = React.useContext(ChartContext)

  if (!context) {
    throw new Error('useChart must be used within a <ChartContainer />')
  }

  return context
}

const ChartContainer = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & {
    config: ChartConfig
    children: React.ComponentProps<
      typeof RechartsPrimitive.ResponsiveContainer
    >['children']
  }
>(({ id, className, children, config, ...props }, ref) => {
  const uniqueId = React.useId()
  const chartId = `chart-${id || uniqueId.replace(/:/g, '')}`

  return (
    <ChartContext.Provider value={{ config }}>
      <div
        data-chart={chartId}
        ref={ref}
        className={cn(
          "[&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-border [&_.recharts-radial-bar-background-sector]:fill-muted [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted [&_.recharts-reference-line_[stroke='#ccc']]:stroke-border flex aspect-video justify-center [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-hidden [&_.recharts-sector]:outline-hidden [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-surface]:outline-hidden",
          className,
        )}
        {...props}
      >
        <ChartStyle id={chartId} config={config} />
        <RechartsPrimitive.ResponsiveContainer>
          {children}
        </RechartsPrimitive.ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  )
})
ChartContainer.displayName = 'Chart'

const ChartStyle = ({ id, config }: { id: string; config: ChartConfig }) => {
  const colorConfig = Object.entries(config).filter(
    ([_, config]) => config.theme || config.color,
  )

  if (!colorConfig.length) {
    return null
  }

  return (
    <style
      dangerouslySetInnerHTML={{
        __html: Object.entries(THEMES)
          .map(
            ([theme, prefix]) => `
${prefix} [data-chart=${id}] {
${colorConfig
  .map(([key, itemConfig]) => {
    const color =
      itemConfig.theme?.[theme as keyof typeof itemConfig.theme] || itemConfig.color
    return color ? `  --color-${key}: ${color};` : null
  })
  .join('\n')}
}
`,
          )
          .join('\n'),
      }}
    />
  )
}

const ChartTooltip = RechartsPrimitive.Tooltip

const ChartTooltipContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof RechartsPrimitive.Tooltip> &
    React.ComponentProps<'div'> & {
      hideLabel?: boolean
      hideIndicator?: boolean
      indicator?: 'line' | 'dot' | 'dashed'
      nameKey?: string
      labelKey?: string
      subLabel?: string
      subLabelValueKey?: string
    }
>(
  (
    {
      active,
      payload,
      className,
      indicator = 'dot',
      hideLabel = false,
      hideIndicator = false,
      label,
      labelFormatter,
      labelClassName,
      formatter,
      color,
      children,
      nameKey,
      labelKey,
      subLabel,
      subLabelValueKey,
    },
    ref,
  ) => {
    const { config } = useChart()

    const tooltipLabel = React.useMemo(() => {
      if (hideLabel || !payload?.length) {
        return null
      }

      const [item] = payload
      const key = `${labelKey || item?.dataKey || item?.name || 'value'}`
      const itemConfig = getPayloadConfigFromPayload(config, item, key)
      const value =
        !labelKey && typeof label === 'string'
          ? config[label as keyof typeof config]?.label || label
          : itemConfig?.label
      const subLabelValue = subLabelValueKey ? item?.payload[subLabelValueKey] : undefined
      if (labelFormatter) {
        return (
          <div className={cn('font-medium', labelClassName)}>
            {labelFormatter(value, payload)}
          </div>
        )
      }

      if (!value) {
        return null
      }

      return (
        <ChartTooltipHeader
          title={value}
          subLabel={subLabel}
          subValue={subLabelValue}
          // subLabel={config.subLabel}
          // subValue={subLabelValue}
        />
      )
    }, [label, labelFormatter, payload, hideLabel, labelClassName, config, labelKey])

    if (!active || !payload?.length) {
      return null
    }

    const nestLabel = payload.length === 1 && indicator !== 'dot'

    return (
      <div
        ref={ref}
        className={cn(
          'border-border/50 bg-background grid min-w-32 items-start gap-1.5 rounded-lg border shadow-xl',
          className,
        )}
      >
        {!nestLabel ? tooltipLabel : null}
        <div className="grid gap-1.5 p-3 pt-0">
          {children ||
            payload.map((item, index) => {
              const key = `${nameKey || item.name || item.dataKey || 'value'}`
              const itemConfig = getPayloadConfigFromPayload(config, item, key)
              const indicatorColor = color || item.payload.fill || item.color
              if (itemConfig?.hidden) return null
              return (
                <div
                  key={item.dataKey}
                  className={cn(
                    '[&>svg]:text-muted-foreground flex w-full flex-wrap items-stretch gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5',
                    indicator === 'dot' && 'items-center',
                  )}
                >
                  {formatter && item?.value !== undefined && item.name ? (
                    formatter(item.value, item.name, item, index, item.payload)
                  ) : (
                    <>
                      {itemConfig?.icon ? (
                        <itemConfig.icon />
                      ) : (
                        !hideIndicator && (
                          <div
                            className={cn(
                              'shrink-0 rounded-[2px] border-(--color-border) bg-(--color-bg)',
                              {
                                'h-4 w-4': indicator === 'dot',
                                'w-1': indicator === 'line',
                                'w-0 border-[1.5px] border-dashed bg-transparent':
                                  indicator === 'dashed',
                                'my-0.5': nestLabel && indicator === 'dashed',
                              },
                            )}
                            style={
                              {
                                '--color-bg': indicatorColor,
                                '--color-border': indicatorColor,
                              } as React.CSSProperties
                            }
                          />
                        )
                      )}
                      <div
                        className={cn(
                          'flex flex-1 justify-between gap-3 leading-none',
                          nestLabel ? 'items-end' : 'items-center',
                        )}
                      >
                        <div className="grid gap-1.5">
                          {nestLabel ? tooltipLabel : null}
                          <span>{itemConfig?.label || item.name}</span>
                        </div>
                        {typeof item.value !== undefined && (
                          <ChartTooltipMetric
                            unit={itemConfig?.unit}
                            value={
                              itemConfig?.formatter
                                ? itemConfig.formatter(item.value as unknown as number)
                                : (item.value as unknown as string)
                            }
                          />
                        )}
                      </div>
                    </>
                  )}
                </div>
              )
            })}
        </div>
      </div>
    )
  },
)
ChartTooltipContent.displayName = 'ChartTooltipContent'

const ChartLegend = RechartsPrimitive.Legend

const ChartLegendContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> &
    Pick<RechartsPrimitive.LegendProps, 'payload' | 'verticalAlign'> & {
      hideIcon?: boolean
      nameKey?: string
    }
>(({ className, hideIcon = false, payload, verticalAlign = 'bottom', nameKey }, ref) => {
  const { config } = useChart()

  if (!payload?.length) {
    return null
  }

  return (
    <div
      ref={ref}
      className={cn(
        'flex items-center justify-center gap-4',
        verticalAlign === 'top' ? 'pb-3' : 'pt-3',
        className,
      )}
    >
      {payload.map((item) => {
        const key = `${nameKey || item.dataKey || 'value'}`
        const itemConfig = getPayloadConfigFromPayload(config, item, key)

        return (
          <div
            key={item.value}
            className={cn(
              '[&>svg]:text-muted-foreground flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3',
            )}
          >
            {itemConfig?.icon && !hideIcon ? (
              <itemConfig.icon />
            ) : (
              <div
                className="h-2 w-2 shrink-0 rounded-[2px]"
                style={{
                  backgroundColor: item.color,
                }}
              />
            )}
            {itemConfig?.label}
          </div>
        )
      })}
    </div>
  )
})
ChartLegendContent.displayName = 'ChartLegend'

// Helper to extract item config from a payload.
function getPayloadConfigFromPayload(config: ChartConfig, payload: unknown, key: string) {
  if (typeof payload !== 'object' || payload === null) {
    return undefined
  }

  const payloadPayload =
    'payload' in payload &&
    typeof payload.payload === 'object' &&
    payload.payload !== null
      ? payload.payload
      : undefined

  let configLabelKey: string = key

  if (key in payload && typeof payload[key as keyof typeof payload] === 'string') {
    configLabelKey = payload[key as keyof typeof payload] as string
  } else if (
    payloadPayload &&
    key in payloadPayload &&
    typeof payloadPayload[key as keyof typeof payloadPayload] === 'string'
  ) {
    configLabelKey = payloadPayload[key as keyof typeof payloadPayload] as string
  }

  return configLabelKey in config
    ? config[configLabelKey]
    : config[key as keyof typeof config]
}

export {
  ChartTooltip,
  ChartTooltipContent,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartStyle,
}
